import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, dniToEmail, functions, registrarActividad } from '../firebase';
import useEsMovil from '../hooks/useEsMovil';
import FranjaMarca, { COLORES_MARCA } from '../components/FranjaMarca';
import { marcarSaludoPendiente } from '../components/SaludoBienvenida';
import CampoClave from '../components/CampoClave';

const estiloInput = {
  width: '100%',
  padding: '12px',
  border: '2px solid #eee',
  borderRadius: '8px',
  fontSize: '16px',
  outline: 'none',
  boxSizing: 'border-box'
};

const botonPrincipal = (cargando) => ({
  width: '100%',
  padding: '14px',
  background: '#E4212C',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 'bold',
  cursor: cargando ? 'default' : 'pointer',
  opacity: cargando ? 0.7 : 1
});

// Link de texto (no un botón blanco suelto).
const linkBoton = {
  display: 'block',
  margin: '16px auto 0',
  background: 'none',
  border: 'none',
  color: COLORES_MARCA.azul,
  fontSize: '14px',
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'pointer',
  padding: '6px'
};

function Login() {
  const navigate = useNavigate();
  const esMovil = useEsMovil();
  const [dni, setDni] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  // "¿Olvidaste tu contraseña?": el pedido le llega a la admin en /admin
  // (no hay mail para mandar un link: el usuario es el DNI).
  const [modoOlvido, setModoOlvido] = useState(false);
  const [olvidoEnviado, setOlvidoEnviado] = useState(false);

  const handlePedirNueva = async (e) => {
    e.preventDefault();
    setError('');
    const dniLimpio = dni.replace(/\D/g, '');
    if (!dniLimpio) { setError('Escribí tu DNI (solo números).'); return; }
    setCargando(true);
    try {
      await httpsCallable(functions, 'solicitarNuevaPasswordStaff')({ dni: dniLimpio });
      setOlvidoEnviado(true);
    } catch (err) {
      if (err.code === 'functions/resource-exhausted' || err.code === 'functions/invalid-argument') {
        setError(err.message);
      } else {
        setError('No se pudo enviar el aviso. Revisá internet y probá de nuevo.');
      }
    } finally {
      setCargando(false);
    }
  };

  const volverAlIngreso = () => {
    setModoOlvido(false);
    setOlvidoEnviado(false);
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setCargando(true);
    // Solo los números del DNI (caso real 2026-10-09: Giselle no podía entrar
    // escribiendo el DNI con puntos "24.070.338" o con espacios).
    const dniLimpio = dni.replace(/\D/g, '');
    if (!dniLimpio) { setError('Escribí tu DNI (solo números).'); setCargando(false); return; }
    try {
      try {
        await signInWithEmailAndPassword(auth, dniToEmail(dniLimpio), password);
      } catch (err) {
        // El celular a veces agrega un espacio al final de la contraseña.
        if (password !== password.trim() && (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password')) {
          await signInWithEmailAndPassword(auth, dniToEmail(dniLimpio), password.trim());
        } else {
          throw err;
        }
      }
      marcarSaludoPendiente();
      await registrarActividad('Inicio de sesión');
      navigate('/dashboard');
    } catch (err) {
      if (err.code === 'auth/too-many-requests') {
        setError('Demasiados intentos seguidos. Esperá unos minutos y probá de nuevo.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('No hay conexión a internet. Revisá el wifi y probá de nuevo.');
      } else if (err.code === 'auth/user-disabled') {
        setError('Este usuario está desactivado. Pedile a la administradora que lo active.');
      } else {
        setError('DNI o contraseña incorrectos. Si no te acordás la contraseña, tocá "¿Olvidaste tu contraseña?".');
      }
    } finally {
      setCargando(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      // Colores plenos del local, sin degradé (Mica, 2026-10-09).
      background: COLORES_MARCA.azul,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Arial, sans-serif',
      padding: esMovil ? '16px' : 0,
      boxSizing: 'border-box',
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        padding: 0,
        width: '100%',
        maxWidth: '420px',
        boxSizing: 'border-box',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <FranjaMarca alto={10} />
        <div style={{ padding: esMovil ? '28px 24px' : '48px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src="/logo-tu-artistica.png" alt="Tu Artística" style={{ height: '44px' }} />
          <p style={{ color: COLORES_MARCA.azul, marginTop: '10px', fontWeight: 'bold' }}>Sistema de Gestión</p>
        </div>

        {modoOlvido ? (
          <div>
            <h3 style={{ margin: '0 0 8px', color: '#1A1A2E', fontSize: '18px' }}>¿Olvidaste tu contraseña?</h3>
            {olvidoEnviado ? (
              <>
                <p style={{ background: '#E8F5E9', color: '#1B5E20', padding: '12px', borderRadius: '8px', fontSize: '14px', lineHeight: 1.4 }}>
                  ¡Listo! Si cargaste un <b>email de respaldo</b>, te llega ahí un link para elegir una
                  contraseña nueva (mirá también en Spam). Si no, le avisamos a la administradora para que
                  te pase una contraseña provisoria.
                </p>
                <button type="button" onClick={volverAlIngreso} style={botonPrincipal(false)}>
                  Volver a ingresar
                </button>
              </>
            ) : (
              <form onSubmit={handlePedirNueva}>
                <p style={{ color: '#555', fontSize: '14px', marginTop: 0, lineHeight: 1.4 }}>
                  Escribí tu DNI y te mandamos un link a tu email de respaldo para elegir una contraseña nueva.
                </p>
                <input
                  type="text"
                  value={dni}
                  onChange={(e) => setDni(e.target.value)}
                  inputMode="numeric"
                  placeholder="Tu DNI (solo números)"
                  required
                  style={{ ...estiloInput, marginBottom: '16px' }}
                />
                {error && <p style={{ color: '#E4212C', fontSize: '14px', marginBottom: '16px' }}>{error}</p>}
                <button type="submit" disabled={cargando} style={botonPrincipal(cargando)}>
                  {cargando ? 'Enviando...' : 'Pedir contraseña nueva'}
                </button>
                <button type="button" onClick={volverAlIngreso} style={linkBoton}>
                  ← Volver
                </button>
              </form>
            )}
          </div>
        ) : (
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontWeight: '500' }}>
              Usuario (DNI)
            </label>
            <input
              type="text"
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              inputMode="numeric"
              autoComplete="off"
              placeholder="Tu DNI (solo números)"
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #eee',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: '#333', fontWeight: '500' }}>
              Contraseña
            </label>
            <CampoClave
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={estiloInput}
            />
          </div>

          {error && (
            <p style={{ color: '#E4212C', fontSize: '14px', marginBottom: '16px' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={cargando}
            style={{
              width: '100%',
              padding: '14px',
              background: '#E4212C',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: cargando ? 'default' : 'pointer',
              opacity: cargando ? 0.7 : 1
            }}
          >
            {cargando ? 'Espera...' : 'Ingresar'}
          </button>
          <button type="button" onClick={() => { setModoOlvido(true); setError(''); }} style={linkBoton}>
            ¿Olvidaste tu contraseña?
          </button>
        </form>
        )}
        </div>
      </div>
    </div>
  );
}

export default Login;
